import type { LaborScan } from "@/lib/labor/laborApi";

import type { CartonSerialRow } from "./prepackApi";

export function formatPrePackLaborDurationMmSs(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes)}:${String(remainder).padStart(2, "0")}`;
}

export type PrePackLaborWorkOrderTableRow = {
  workOrderLabel: string;
  serialCount: number;
  hypotheticalSeconds: number;
};

function workOrderLabelForSample(sample: CartonSerialRow): string {
  if (sample.build_order_id == null) {
    return "No work order";
  }
  const id = String(sample.build_order_id);
  const ref = sample.mv_reference_number?.trim();
  if (ref) {
    return `Work order ${id} · ${ref}`;
  }
  return `Work order ${id}`;
}

/**
 * Groups boxed carton serial lines by `build_order_id` and assigns a hypothetical share of elapsed seconds
 * weighted by line count (same weighting as a naive per-line split at session end).
 */
export function buildPrePackLaborAllocationTableRows(
  elapsedSeconds: number,
  serials: CartonSerialRow[],
): PrePackLaborWorkOrderTableRow[] {
  if (serials.length === 0) {
    return [];
  }

  type Group = { rows: CartonSerialRow[] };
  const groupOrder: Group[] = [];
  const groupIndex = new Map<string, number>();
  for (const row of serials) {
    const key = row.build_order_id == null ? "none" : String(row.build_order_id);
    const existing = groupIndex.get(key);
    if (existing === undefined) {
      groupIndex.set(key, groupOrder.length);
      groupOrder.push({ rows: [row] });
    } else {
      groupOrder[existing]!.rows.push(row);
    }
  }

  const weights = groupOrder.map((group) => group.rows.length);
  const weightSum = weights.reduce((accumulator, weight) => accumulator + weight, 0);
  const shares =
    weightSum > 0
      ? (() => {
          const base = weights.map((weight) => Math.floor((elapsedSeconds * weight) / weightSum));
          let used = base.reduce((accumulator, value) => accumulator + value, 0);
          let remainder = elapsedSeconds - used;
          const out = [...base];
          let index = 0;
          while (remainder > 0) {
            out[index % out.length] = (out[index % out.length] ?? 0) + 1;
            remainder -= 1;
            index += 1;
          }
          return out;
        })()
      : weights.map(() => 0);

  return groupOrder.map((group, groupPosition) => {
    const sample = group.rows[0]!;
    const share = shares[groupPosition] ?? 0;
    return {
      workOrderLabel: workOrderLabelForSample(sample),
      serialCount: group.rows.length,
      hypotheticalSeconds: share,
    };
  });
}

/** Compact description for the help button `aria-label`. */
export function buildPrePackLaborStationHoverAriaLabel(
  elapsedSeconds: number,
  rows: PrePackLaborWorkOrderTableRow[],
  scans: LaborScan[],
): string {
  const gross = formatPrePackLaborDurationMmSs(elapsedSeconds);
  if (rows.length === 0) {
    const scanNote = scans.length > 0 ? ` ${String(scans.length)} server scan rows.` : "";
    return `Pre-pack station: gross elapsed ${gross}. No boxed serials in this carton.${scanNote}`;
  }
  const rowParts = rows.map(
    (row) =>
      `${row.workOrderLabel}, ${String(row.serialCount)} serials, ${formatPrePackLaborDurationMmSs(row.hypotheticalSeconds)} hypothetical`,
  );
  const scanNote = scans.length > 0 ? ` Server scans: ${String(scans.length)}.` : "";
  return `Pre-pack station: gross elapsed ${gross}. ${rowParts.join(". ")}.${scanNote}`;
}
